import React, { useEffect, useRef } from 'react';

/**
 * Shared Bloch-sphere canvas: draws the X-Z plane (Y isn't drawn on a 2D
 * canvas, only referenced), with an optional "mixed" indicator for a qubit
 * whose Bloch vector has collapsed to (near) zero length — i.e. it's part
 * of an entangled system and has no individual definite state.
 */
export default function BlochSphereCanvas({ vector, size = 220, showMixed = true }) {
  const canvasRef = useRef(null);
  const len = Math.sqrt(vector.x * vector.x + vector.y * vector.y + vector.z * vector.z);
  const isMixed = showMixed && len < 0.05;
  const isPole = len > 0.98;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const width = canvas.width, height = canvas.height;
    const centerX = width / 2, centerY = height / 2;
    const radius = width * 0.4;

    ctx.clearRect(0, 0, width, height);

    const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
    gradient.addColorStop(0, 'rgba(92, 45, 145, 0.10)');
    gradient.addColorStop(1, 'rgba(60, 60, 60, 0.03)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(0, 120, 212, 0.5)';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.strokeStyle = 'rgba(100, 100, 100, 0.4)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(centerX - radius, centerY);
    ctx.lineTo(centerX + radius, centerY);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(centerX - radius * 0.7, centerY - 16);
    ctx.lineTo(centerX + radius * 0.7, centerY + 16);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(centerX, centerY - radius);
    ctx.lineTo(centerX, centerY + radius);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = 'rgba(8, 145, 178, 1)';
    ctx.font = 'bold 14px sans-serif';
    ctx.fillText('|0⟩', centerX - 12, centerY - radius - 8);
    ctx.fillStyle = 'rgba(219, 39, 119, 1)';
    ctx.fillText('|1⟩', centerX - 12, centerY + radius + 20);
    ctx.fillStyle = 'rgba(90, 90, 90, 0.9)';
    ctx.font = 'bold 13px sans-serif';
    ctx.fillText('X', centerX + radius + 8, centerY + 4);
    ctx.fillText('Y', centerX - radius * 0.7 - 22, centerY - 20);

    if (isMixed) {
      ctx.strokeStyle = 'rgba(107, 114, 128, 0.6)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([2, 2]);
      ctx.beginPath();
      ctx.arc(centerX, centerY, 6, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = 'rgba(107, 114, 128, 0.8)';
      ctx.font = '11px sans-serif';
      ctx.fillText('mixed', centerX - 14, centerY + 20);
      return;
    }

    const vx = vector.x * radius;
    const vz = -vector.z * radius;
    const color = isPole ? (vector.z > 0 ? 'rgba(8, 145, 178, 1)' : 'rgba(219, 39, 119, 1)') : 'rgba(217, 119, 6, 1)';
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(centerX + vx, centerY + vz);
    ctx.stroke();
    const angle = Math.atan2(vz, vx);
    const headLen = 9;
    ctx.beginPath();
    ctx.moveTo(centerX + vx, centerY + vz);
    ctx.lineTo(centerX + vx - headLen * Math.cos(angle - Math.PI / 6), centerY + vz - headLen * Math.sin(angle - Math.PI / 6));
    ctx.moveTo(centerX + vx, centerY + vz);
    ctx.lineTo(centerX + vx - headLen * Math.cos(angle + Math.PI / 6), centerY + vz - headLen * Math.sin(angle + Math.PI / 6));
    ctx.stroke();
  }, [vector, isMixed, isPole, size]);

  return <canvas ref={canvasRef} width={size} height={size} className="rounded-lg" />;
}
